import {Color, Icon} from '@benchling/nucleus-ui';
import {StyleSheet, css} from 'aphrodite';
import React, {useCallback, useEffect, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {ELN_ANALYTICS_ACTIONS} from 'constants/analytics-products';
import AICheckerResults from 'notebook/ai-tools/AICheckerResults';
import {CheckEntryResponseState} from 'notebook/ai-tools/NotebookAIToolsConstants';
import useCheckEntryResultInfo from 'notebook/ai-tools/useCheckEntryResultInfo';
// @ts-expect-error
import EntryStore from 'stores/EntryStore';
import {FONT_PROXIMA_16, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface AICheckerResultsContainerProps {
  entryId: string;
}

/**
 * Top-level UI for the AI Notebook Checker results. This component is mounted within the review tab
 * and is responsible for fetching and displaying results, and sending relevant analytics events.
 *
 * See useCheckEntryResultInfo for details on the data fetching lifecycle.
 */
export default function AICheckerResultsContainer({entryId}: AICheckerResultsContainerProps): JSX.Element {
  const {checkEntryResults, onToggleCompleted, onToggleUsefulState, onRecompute} =
    useCheckEntryResultInfo(entryId);
  const [isCollapsed, setCollapsed] = useState(false);
  const [openVersionId, setOpenVersionId] = useState<number | null>(null);

  const {state} = checkEntryResults;
  const traceId = state === CheckEntryResponseState.SUCCESS ? checkEntryResults.traceId : null;
  const checkedAt = state === CheckEntryResponseState.SUCCESS ? checkEntryResults.checkedAt : null;
  const checkedVersion = state === CheckEntryResponseState.SUCCESS ? checkEntryResults.checkedVersion : null;
  const checkedAtDate = checkedAt != null ? new Date(checkedAt) : null;

  const rerunChecks = useCallback(() => {
    trackEvent(ELN_ANALYTICS_ACTIONS.RERAN_AI_NOTEBOOK_CHECKER, {
      traceId: state === CheckEntryResponseState.SUCCESS ? traceId : null,
      entryId,
    });
    onRecompute();
  }, [state, traceId, entryId, onRecompute]);

  // Update the modification timestamp when the component mounts
  useEffect(() => {
    // Subscribe to entry updates to refresh the timestamp when the entry changes
    const handleEntryUpdate = (): void => {
      if (EntryStore.inStore(entryId)) {
        const versionState = EntryStore.getVersionState(entryId);
        setOpenVersionId(versionState.openVersionId);
      }
    };

    // Add listener for entry updates
    EntryStore.addChangeListener(handleEntryUpdate);

    // Remove listener on cleanup
    return () => {
      EntryStore.removeChangeListener(handleEntryUpdate);
    };
  }, [checkedVersion, entryId]);

  const isCheckResultStale =
    checkedVersion != null && openVersionId != null && checkedVersion < openVersionId;
  return (
    <div
      className={css(styles.resultsContainer)}
      data-test-component="AICheckerResultsContainer"
      data-test-element="root"
    >
      <div className={css(styles.titleBar)}>
        <div className={css(styles.title)}>
          <h4>Notebook Check</h4>
          <Icon icon="sparkle" className={css(styles.titleSparkle)} />
        </div>
        <div className={css(styles.expandCollapseIcon)}>
          <BenchlingIcon
            icon={isCollapsed ? 'chevron-down' : 'chevron-up'}
            altText={isCollapsed ? 'Expand' : 'Collapse'}
            onClick={() => {
              setCollapsed((collapsed) => !collapsed);
            }}
          />
        </div>
      </div>
      {!isCollapsed && (
        <>
          <div className={css(styles.titleBar)}>
            <div className={css(styles.flexRow)}>
              <Icon icon="sparkle" className={css(styles.graySparkle)} />
              <strong>Suggestions</strong>
            </div>

            <div className={css(styles.flexRow)}>
              {checkedAtDate != null && !isCheckResultStale && (
                <div className={css(styles.statusMessage)}>
                  <span
                    title={checkedAtDate.toLocaleString()}
                    data-test-component="AICheckerResultsContainer"
                    data-test-element="last-refreshed"
                  >
                    Last updated{' '}
                    {checkedAtDate.toLocaleString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                    })}
                  </span>
                </div>
              )}
              {isCheckResultStale && (
                <div
                  className={css(styles.statusMessage)}
                  data-test-component="AICheckerResultsContainer"
                  data-test-element="stale-indicator"
                >
                  <BenchlingIcon icon="warning" className={css(styles.warningIcon)} />
                  <Tooltip text="Entry has been modified since the last check. Consider re-running checks for updated results.">
                    <span>Results may be outdated</span>
                  </Tooltip>
                </div>
              )}
              <Button
                size={ButtonSize.SMALL}
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                icon="refresh"
                onClick={rerunChecks}
                disabled={state === CheckEntryResponseState.LOADING}
                text="Refresh"
              />
            </div>
          </div>

          <div>
            <AICheckerResults
              checkEntryResults={checkEntryResults}
              onToggleCompleted={onToggleCompleted}
              onToggleUsefulState={onToggleUsefulState}
              onRecompute={onRecompute}
            />
          </div>

          {checkEntryResults.state === CheckEntryResponseState.SUCCESS && (
            <p className={css(styles.subtleText)}>
              This feature uses AI and may make mistakes. Always verify the results.{' '}
              <a
                href="https://help.benchling.com/hc/en-us/articles/27659453707405"
                target="_blank"
                rel="noopener noreferrer"
                className={css(styles.learnMoreLink)}
              >
                Learn more
                <BenchlingIcon icon="arrow-up-right-from-square" />
              </a>
            </p>
          )}
        </>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  resultsContainer: {
    borderColor: Color.GRAY_300,
    borderRadius: 4,
    borderStyle: 'solid',
    borderWidth: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 8,
    padding: 16,
  },
  titleBar: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
  },
  titleSparkle: {
    color: Color.PURPLE_500,
    height: 20,
  },
  graySparkle: {
    color: Color.GRAY_400,
  },
  flexRow: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
  },
  expandCollapseIcon: {
    cursor: 'pointer',
    ...FONT_PROXIMA_16,
  },
  title: {
    alignItems: 'center',
    display: 'flex',
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    gap: 8,
  },
  statusMessage: {
    alignItems: 'center',
    color: Color.GRAY_600,
    display: 'flex',
    gap: 8,
  },
  subtleText: {
    color: Color.GRAY_500,
    margin: 0,
  },
  warningIcon: {
    color: Color.ORANGE_400,
    height: 16,
  },
  learnMoreLink: {
    alignItems: 'center',
    display: 'inline-flex',
    gap: 4,
  },
});
